import * as React from 'react'
import { cn } from '@/lib/utils'

/* ─────────────────────────── TYPES ─────────────────────────── */

interface LoadingSpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg'
  fullPage?: boolean
}

/* ─────────── 1. Infinity Trace — Full-page brand loader ─────────── */

/**
 * An animated ∞ infinity symbol drawn with an orange gradient stroke.
 * Used for the main app loading state. The SVG path is an infinity
 * figure-eight that animates via `stroke-dasharray` / `stroke-dashoffset`.
 */
export function InfinityLoader({
  className,
  label = 'Loading workspace…',
}: {
  className?: string
  label?: string
}) {
  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center',
        'bg-neutral-950/60 backdrop-blur-lg',
        className
      )}
    >
      <div className="flex flex-col items-center gap-6">
        {/* Subtle ambient glow behind the symbol */}
        <div className="absolute w-40 h-40 rounded-full bg-orange-500/10 blur-3xl animate-pulse-glow" />

        {/* Infinity SVG */}
        <div className="relative animate-infinity-breathe">
          <svg
            width="80"
            height="44"
            viewBox="0 0 80 44"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="drop-shadow-lg"
          >
            <defs>
              <linearGradient id="infinity-grad" x1="0" y1="0" x2="80" y2="44" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#f97316" />
                <stop offset="50%" stopColor="#fb923c" />
                <stop offset="100%" stopColor="#ea580c" />
              </linearGradient>
            </defs>
            {/* 
              Infinity path: two circular lobes connected at center.
              Path length ≈ 360 (tuned to match dasharray in CSS).
            */}
            <path
              d="M20 22
                 C20 10, 40 2, 40 22
                 C40 42, 60 34, 60 22
                 C60 10, 40 2, 40 22
                 C40 42, 20 34, 20 22Z"
              stroke="url(#infinity-grad)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              className="animate-infinity-draw"
              pathLength="360"
            />
          </svg>
        </div>

        {/* Brand label */}
        <span className="text-[11px] font-semibold tracking-[0.25em] uppercase text-white/50 animate-pulse">
          {label}
        </span>
      </div>
    </div>
  )
}

/* ─────────── 2. Orbital Spinner — Inline loading indicator ─────────── */

/**
 * The original orbital ring spinner for inline / component-level use.
 * Supports sm / md / lg sizes and an optional fullPage overlay mode.
 */
export function LoadingSpinner({
  className,
  size = 'md',
  fullPage = false,
  ...props
}: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: 'h-6 w-6 border-2',
    md: 'h-10 w-10 border-3',
    lg: 'h-16 w-16 border-4',
  }

  const spinner = (
    <div className="relative flex items-center justify-center">
      {/* Outer spinning ring with gradient */}
      <div
        className={cn(
          'animate-spin rounded-full border-t-orange-500 border-r-orange-500/30 border-b-orange-500/10 border-l-orange-500/50',
          sizeClasses[size]
        )}
      />
      {/* Inner glowing pulse dot */}
      <div
        className={cn(
          'absolute rounded-full bg-orange-500 animate-ping opacity-75',
          size === 'sm' ? 'h-2 w-2' : size === 'md' ? 'h-3.5 w-3.5' : 'h-5 w-5'
        )}
      />
      {/* Inner solid dot */}
      <div
        className={cn(
          'absolute rounded-full bg-orange-500',
          size === 'sm' ? 'h-1.5 w-1.5' : size === 'md' ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'
        )}
      />
    </div>
  )

  if (fullPage) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/40 backdrop-blur-md">
        <div className="flex flex-col items-center gap-3 p-6 rounded-2xl bg-neutral-900/60 backdrop-blur-xl ring-1 ring-white/10 shadow-xl shadow-orange-500/5">
          {spinner}
          <span className="text-[10px] font-semibold text-neutral-300 animate-pulse tracking-widest uppercase">
            Loading System
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex items-center justify-center p-4', className)} {...props}>
      {spinner}
    </div>
  )
}

/* ─────────── 3. Tab Loader — the standard in-page loading state ─────────── */

/**
 * The one loading indicator every tab should use while fetching.
 *
 * Before this existed each tab hand-rolled `<Loader2 className="h-8 w-8
 * animate-spin text-orange-XXX" />`, and the shade drifted between 300/400/500
 * across files. Render this inside the page's PageShell (not instead of it) so
 * the header and ambient mesh stay put while data loads.
 */
export function TabLoader({ className, label }: { className?: string; label?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3 py-12', className)} role="status">
      <LoadingSpinner size="md" />
      <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400 dark:text-white/40">
        {label ?? 'Loading'}
      </span>
    </div>
  )
}

/* ─────────── 4. Empty State — the standard "no records" panel ─────────── */

/**
 * Consistent empty state. Replaces the bare `<p>No records</p>` that several
 * tabs used, which read as a rendering bug rather than an intentional state.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: {
  icon?: React.ComponentType<{ className?: string }>
  title: string
  description?: string
  action?: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-2 px-6 py-12 text-center', className)}>
      {Icon && (
        <div className="mb-1 rounded-xl bg-neutral-100 p-3 text-neutral-400 dark:bg-white/[0.03] dark:text-white/30">
          <Icon className="h-6 w-6" />
        </div>
      )}
      <p className="text-sm font-medium text-neutral-700 dark:text-white/70">{title}</p>
      {description && (
        <p className="max-w-sm text-xs text-neutral-500 dark:text-white/40">{description}</p>
      )}
      {action && <div className="mt-3">{action}</div>}
    </div>
  )
}

/* ─────────── 5. Skeleton Shimmer — Content placeholder cards ─────────── */

/**
 * A single skeleton block with the warm amber shimmer sweep.
 * Use as a content placeholder while data loads.
 */
export function SkeletonCard({
  className,
  height = 'h-32',
}: {
  className?: string
  height?: string
}) {
  return (
    <div
      className={cn(
        'skeleton-shimmer rounded-xl',
        height,
        className
      )}
    />
  )
}

/**
 * A full dashboard-shaped skeleton layout with multiple shimmer cards.
 * Mimics the dashboard grid to reduce perceived load time.
 */
export function SkeletonDashboard({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-6 p-6', className)}>
      {/* Top stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SkeletonCard height="h-24" />
        <SkeletonCard height="h-24" />
        <SkeletonCard height="h-24" />
        <SkeletonCard height="h-24" />
      </div>

      {/* Main content area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SkeletonCard height="h-64" className="lg:col-span-2" />
        <SkeletonCard height="h-64" />
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SkeletonCard height="h-48" />
        <SkeletonCard height="h-48" />
      </div>
    </div>
  )
}
